import type { ReactNode } from 'react';

import * as React from 'react';
import { faMessageBot, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeScalableIcon, IconButton } from '@mend/mui';
import { Box, Divider, Drawer as MuiDrawer, Stack, Typography, useTheme } from '@mui/material';
import { z } from 'zod';

import { config } from '#config.ts';

const dataAgentDrawerHeight = '80vh';

export function Drawer(props: { open: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <MuiDrawer
      anchor="bottom"
      open={props.open}
      onClose={props.onClose}
      slotProps={{
        root: { keepMounted: true },
        paper: { sx: { height: dataAgentDrawerHeight } },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ paddingX: 2, paddingY: 1 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FontAwesomeScalableIcon
              icon={faMessageBot}
              fontSize="small"
              sx={{ color: 'quaternary.main' }}
            />
            <Typography component="h2" variant="body1">
              Ask Coordinate
            </Typography>
          </Stack>

          <IconButton aria-label="Close Coordinate drawer" onClick={props.onClose}>
            <FontAwesomeScalableIcon icon={faXmark} />
          </IconButton>
        </Stack>

        <Divider aria-hidden="true" />

        <Box sx={{ flex: 1, minHeight: 0, padding: 2 }}>{props.children}</Box>
      </Box>
    </MuiDrawer>
  );
}

const iframeSx = {
  width: '100%',
  height: '100%',
  border: 0,
  bgcolor: 'background.default',
} as const;

const dataAgentMessageSchema = z.object({ type: z.literal('mia:data-agent:response') });

export function Iframe(props: {
  initialPrivateContext?: string;
  initialPrompt?: string;
  isInitialAutoScrollEnabled?: boolean;
  onResponse?: () => void;
}) {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [iframeSearchParams] = React.useState(() => {
    const miaSearchParams = new URLSearchParams();
    miaSearchParams.set('slim', '1');
    if (props.initialPrompt) {
      miaSearchParams.set('prompt', props.initialPrompt);
    }
    if (props.initialPrivateContext) {
      miaSearchParams.set('privateContext', props.initialPrivateContext);
    }
    if (props.isInitialAutoScrollEnabled === false) {
      miaSearchParams.set('initialAutoScroll', '0');
    }

    // Hash route: mia-web is a hash-routed SPA on a static bucket, so the route +
    // query live in the fragment. The bucket only serves `/` (200); a path like
    // `/data-agent` 404s, which the iframe refuses to render.
    return miaSearchParams;
  });

  const theme = useTheme();
  iframeSearchParams.set('theme', theme.palette.mode);

  const onResponseRef = React.useRef(props.onResponse);

  React.useLayoutEffect(() => {
    onResponseRef.current = props.onResponse;
  });

  React.useEffect(() => {
    function handler(event: MessageEvent) {
      // Only the embedded mia-web app may report responses — any other frame or page that can
      // post to this window could otherwise spoof one.
      if (
        !onResponseRef.current ||
        event.origin !== new URL(config.MIA_WEB_BASE_URL).origin ||
        event.source !== iframeRef.current?.contentWindow
      ) {
        return;
      }

      const parsed = dataAgentMessageSchema.safeParse(event.data);
      if (parsed.success) {
        onResponseRef.current();
      }
    }

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return (
    <Box
      ref={iframeRef}
      component="iframe"
      title="Coordinate assistant"
      src={`${config.MIA_WEB_BASE_URL}/#/data-agent?${iframeSearchParams.toString()}`}
      sx={iframeSx}
    />
  );
}
